import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface Member {
  id: string;
  username: string;
  nickname: string | null;
  phone: string | null;
  avatar_url: string | null;
  level: string;
  points: number;
  balance: number;
  status: string;
  created_at: string;
}

interface SubordinatesDrawerProps {
  member: Member;
  onClose: () => void;
}

const levelColor: Record<string, string> = {
  '会员': 'bg-gray-100 text-gray-500',
  '销售员': 'bg-emerald-50 text-emerald-600',
  '批发': 'bg-sky-50 text-sky-600',
  '主管': 'bg-amber-50 text-amber-600',
  '经理': 'bg-orange-50 text-orange-600',
  '总监': 'bg-rose-50 text-rose-600',
  '董事': 'bg-violet-50 text-violet-600',
  '总裁': 'bg-red-50 text-red-600',
};

const SubordinatesDrawer = ({ member, onClose }: SubordinatesDrawerProps) => {
  const [subs, setSubs] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchSubs = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('custom_users')
        .select('id, username, nickname, phone, avatar_url, level, points, balance, status, created_at')
        .eq('referrer_id', member.id)
        .order('created_at', { ascending: false });
      setSubs((data as Member[]) ?? []);
      setLoading(false);
    };
    fetchSubs();
  }, [member.id]);

  const filtered = subs.filter(s =>
    !search ||
    (s.nickname ?? '').includes(search) ||
    (s.username ?? '').includes(search) ||
    (s.phone ?? '').includes(search)
  );

  const formatDate = (ts: string) =>
    new Date(ts).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div
        className="fixed top-0 right-0 h-full w-[460px] bg-white z-50 flex flex-col"
        style={{ animation: 'slideInRight 0.25s ease' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center cursor-pointer text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-gray-900 text-base">下级信息</h3>
            <p className="text-xs text-gray-400 truncate">
              {member.nickname ?? member.username} 的直属下级
            </p>
          </div>
          <span className="text-sm font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full">
            共 {subs.length} 人
          </span>
        </div>

        {/* 统计 */}
        <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 flex-shrink-0">
          {[
            { label: '下级总数', value: subs.length, color: 'text-gray-800' },
            { label: '正常会员', value: subs.filter(s => s.status === 'active').length, color: 'text-emerald-600' },
            { label: '总积分', value: subs.reduce((a, s) => a + s.points, 0).toLocaleString(), color: 'text-rose-500' },
          ].map(s => (
            <div key={s.label} className="py-3 text-center">
              <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* 搜索 */}
        <div className="px-4 py-3 border-b border-gray-50 flex-shrink-0">
          <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
            <i className="ri-search-line text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索昵称/用户名/手机号"
              className="text-sm outline-none bg-transparent flex-1 placeholder-gray-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <i className="ri-team-line text-4xl mb-3" />
              <p className="text-sm">{subs.length === 0 ? '暂无下级成员' : '没有匹配的结果'}</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50/60 border-b border-gray-100">
                <tr>
                  <th className="text-left text-xs text-gray-400 font-medium px-5 py-3">会员</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">等级</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">积分</th>
                  <th className="text-left text-xs text-gray-400 font-medium px-4 py-3">注册时间</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id} className={`${i < filtered.length - 1 ? 'border-b border-gray-50' : ''} hover:bg-gray-50/50`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-orange-300 flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {s.avatar_url ? (
                            <img src={s.avatar_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white text-xs font-bold">
                              {(s.nickname ?? s.username ?? '?')[0].toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-sm text-gray-800 font-medium">{s.nickname ?? s.username}</p>
                          <p className="text-xs text-gray-400">{s.phone ?? `@${s.username}`}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${levelColor[s.level] ?? 'bg-gray-100 text-gray-500'}`}>
                        {s.level}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-rose-500 font-semibold">{s.points.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-400 whitespace-nowrap">{formatDate(s.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <style>{`@keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
};

export default SubordinatesDrawer;
